<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { ApiRequestError, listAdminIntakes, revokeAdminIntake } from '@/api/client'
import type { AdminIntake, AdminIntakePage } from '@/api/schemas'
import { useToasts } from '@/lib/toasts'
import { formatLocalTime } from './intakeHelpers'

const result = ref<AdminIntakePage | null>(null)
const error = ref('')
const loading = ref(true)
const toasts = useToasts()
// Only the clipboard fallback stays on the page: it points at the field it reveals.
const copyNotice = ref('')
const copyUrl = ref('')
const revoking = ref<AdminIntake | null>(null)
const revokeBusy = ref(false)
const revokeDialog = ref<HTMLDialogElement | null>(null)
let revokeTrigger: HTMLElement | undefined
const pageCount = computed(() =>
  result.value ? Math.max(1, Math.ceil(result.value.total / result.value.page_size)) : 1,
)

function documentsSummary(intake: AdminIntake): string {
  const files = `${intake.documents.file_count} file${intake.documents.file_count === 1 ? '' : 's'}`
  const missing = intake.documents.missing_required
  return missing.length
    ? `${files} · missing: ${missing.join(', ')}`
    : `${files} · nothing required missing`
}

async function load(page = 1) {
  loading.value = true
  error.value = ''
  try {
    result.value = await listAdminIntakes(page)
  } catch (cause) {
    error.value = cause instanceof ApiRequestError ? cause.message : 'Unable to load intake links.'
  } finally {
    loading.value = false
  }
}
async function copy(url: string) {
  copyNotice.value = ''
  copyUrl.value = ''
  try {
    await navigator.clipboard.writeText(url)
    toasts.success('Link copied.')
  } catch {
    copyUrl.value = url
    copyNotice.value = 'Clipboard access was unavailable. Select and copy the link below.'
  }
}
function openRevoke(intake: AdminIntake, event: MouseEvent) {
  revokeTrigger = event.currentTarget as HTMLElement
  revoking.value = intake
}
function closeRevoke() {
  revokeDialog.value?.close()
}
watch(revoking, async (intake) => {
  if (intake) {
    await nextTick()
    revokeDialog.value?.showModal()
  }
})
function dialogClosed() {
  revoking.value = null
  revokeTrigger?.focus()
  revokeTrigger = undefined
}
async function revoke() {
  if (!revoking.value) return
  revokeBusy.value = true
  error.value = ''
  try {
    await revokeAdminIntake(revoking.value.id)
    closeRevoke()
    await load(result.value?.page ?? 1)
  } catch (cause) {
    error.value =
      cause instanceof ApiRequestError ? cause.message : 'Unable to revoke this intake link.'
  } finally {
    revokeBusy.value = false
  }
}
onMounted(() => load())
</script>

<template>
  <main class="admin-page">
    <header class="admin-header admin-header-row">
      <div>
        <p class="eyebrow">Administration</p>
        <h1 class="display">Intake links</h1>
        <p>Client intake access is managed here.</p>
      </div>
      <div class="header-actions">
        <RouterLink class="btn btn-secondary" to="/account">Account</RouterLink
        ><RouterLink class="btn btn-primary" to="/admin/intakes/new">Create intake link</RouterLink>
      </div>
    </header>
    <p v-if="error" class="form-error" role="alert">{{ error }}</p>
    <p v-if="copyNotice" class="form-notice" role="status">{{ copyNotice }}</p>
    <section v-if="loading" class="admin-card" aria-busy="true">Loading intake links…</section>
    <section v-else-if="!result?.intakes.length" class="admin-card empty-state">
      <h2>No intake links yet</h2>
      <p>Create a link when a client is ready to complete their intake.</p>
      <RouterLink class="btn btn-primary" to="/admin/intakes/new">Create intake link</RouterLink>
    </section>
    <template v-else>
      <div class="intake-table-wrap">
        <table class="intake-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Template</th>
              <th>Status</th>
              <th>Expiry</th>
              <th>Created</th>
              <th>Documents</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="intake in result?.intakes" :key="intake.id">
              <th scope="row">
                <RouterLink :to="{ name: 'admin-intake-detail', params: { id: intake.id } }">{{
                  intake.client_name
                }}</RouterLink>
              </th>
              <td>{{ intake.form_type }}</td>
              <td>
                <span class="status" :class="`status-${intake.status}`">{{ intake.status }}</span>
              </td>
              <td>{{ formatLocalTime(intake.expires_at) }}</td>
              <td>{{ formatLocalTime(intake.created_at) }}</td>
              <td>{{ documentsSummary(intake) }}</td>
              <td class="actions">
                <RouterLink
                  class="link-button"
                  :to="{ name: 'admin-intake-detail', params: { id: intake.id } }"
                  >View answers</RouterLink
                >
                <button class="link-button" type="button" @click="copy(intake.url)">
                  Copy link</button
                ><input
                  v-if="copyUrl === intake.url"
                  class="copy-field list-copy-field"
                  readonly
                  aria-label="Intake link to select and copy"
                  :value="intake.url"
                  @focus="($event.target as HTMLInputElement).select()"
                /><button
                  v-if="intake.status !== 'revoked'"
                  class="link-button danger"
                  type="button"
                  @click="openRevoke(intake, $event)"
                >
                  Revoke link
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <nav v-if="pageCount > 1" class="pagination" aria-label="Intake pages">
        <button
          class="btn btn-secondary"
          :disabled="result!.page <= 1"
          @click="load(result!.page - 1)"
        >
          Previous</button
        ><span>Page {{ result!.page }} of {{ pageCount }}</span
        ><button
          class="btn btn-secondary"
          :disabled="result!.page >= pageCount"
          @click="load(result!.page + 1)"
        >
          Next
        </button>
      </nav>
    </template>
    <dialog
      ref="revokeDialog"
      class="confirm-dialog"
      aria-labelledby="revoke-title"
      @close="dialogClosed"
    >
      <template v-if="revoking"
        ><h2 id="revoke-title">Revoke {{ revoking.client_name }}’s intake link?</h2>
        <p>The link will stop working. Saved responses and document records stay in place.</p>
        <div class="dialog-actions">
          <button class="btn btn-secondary" :disabled="revokeBusy" @click="closeRevoke">
            Cancel</button
          ><button class="btn btn-danger" :disabled="revokeBusy" @click="revoke">
            {{ revokeBusy ? 'Revoking…' : 'Revoke link' }}
          </button>
        </div></template
      >
    </dialog>
  </main>
</template>
